import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { convertEpochToDateString } from '@functions/functions';
import { DatePickerPresets, GlobalFunctions, makeDateSmall, QueryParamType } from '@global/GlobalFunctions';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, from, Subject, take, takeUntil } from 'rxjs';
import { CmmsService, ICaseFile, iCauseTypes, iTriggerTypes } from '@services/cmms.service';
import { IAccount } from '@services/accounts.service';

/** CaseFilters */
interface CaseFilters {
	filter?: string;
	// accountGUID?: string;
	// organisationGUID?: string;
	from?: number;
	to?: number;
	open?: boolean;
	closed?: boolean;
	CMMS?: boolean;
	NotCMMS?: boolean;
	all?: boolean;
	pageIndex?: number;
	pageSize?: number;
}

/** SAPCaseListComponent */
@Component({
	selector: 'app-sap-case-list',
	templateUrl: './sap-case-list.component.html',
	styleUrl: './sap-case-list.component.scss'
})
export class SAPCaseListComponent implements OnInit, OnDestroy {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** accountGUID */
	public accountGUID: string;

	/** organisationUID */
	public organisationUID: string;

	/** cases */
	public cmmsCases: ICaseFile[] = [];

	/** filteredActions */
	public filteredActions: ICaseFile[] = [];

	/** fields */
	public fields: { text: string, value: string } = { text: "ActionType", value: "ActionID" };

	/** triggerTypes - values correspond to value in db table */
	public triggerTypes: iTriggerTypes[] = [];

	/** triggerTypes - values correspond to value in db table */
	public causeTypes: iCauseTypes[] = [];

	/** titleText */
	public titleText: string = "SAP Case History";

	/** useTable */
	public useTable: boolean = true;

	/** datasource */
	public dataSource: MatTableDataSource<ICaseFile>;

	/** Table paginator */
	@ViewChild('paginator') paginator: MatPaginator;
	/** Table sorting */
	@ViewChild(MatSort) sort: MatSort;

	/** displayedColumns */
	displayedColumns: string[] = ['IsOpen', 'InCMMS', 'AssetID', 'Trigger', 'OpenedAt', 'Description', 'CMMSCreatedAt', 'Cause'];

	/** currentUser */
	public currentUser: WebAppUser;

	/** filters */
	public filters: CaseFilters = {};

	public caseFilterTypes: Record<keyof CaseFilters, QueryParamType> = {
		filter: 'string',
		// accountGUID: 'string',
		// organisationGUID: 'string',
		from: 'number',
		to: 'number',
		open: 'boolean',
		closed: 'boolean',
		CMMS: 'boolean',
		NotCMMS: 'boolean',
		all: 'boolean',
		pageIndex: 'number',
		pageSize: 'number',
	};

	/** Define start date */
	public startDate: Date = new Date();
	/** Define end date */
	public endDate: Date = new Date();
	/** queryFrom */
	private queryFrom: number = 0;
	/** queryTo */
	private queryTo: number = 0;

	/** maxMonths */
	private maxMonths: number = 36; // max months increased as retrieving data for long periods no longer an issue

	/** formattedStartDate */
	formattedStartDate: string | undefined;
	/** formattedEndDate */
	formattedEndDate: string | undefined;

	/** isFiltering */
	public isFiltering: boolean = false;

	/** today - #71 */
	public today: Date = new Date();

	/** datePickerPresets - #71 */
	public datePickerPresets = DatePickerPresets;

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	/**
	 * Default Constructor
	 * @param authenticationService authenticationService
	 * @param siteSubscriptions SiteSubscriptions
	 * @param router router
	 * @param route ActivatedRoute,
	 * @param globalFunctions globalFunctions
	 * @param cmmsService CmmsService
	 */
	constructor(
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions,
		private cmmsService: CmmsService
	) {
		this.today.setHours(0, 0, 0, 0);
	}

	/**
	 * ngOnInit
	 * Initializes the component by subscribing to various observables that provide necessary data such as account and organization information, 
	 * hierarchical data, current user details, and query parameters. 
	 * It uses `combineLatest` to ensure that all required data is available before proceeding with the initialization logic. 
	 * The function also includes error handling to manage any issues that arise during the subscription process. 
	 * Additionally, it sets up a subscription to query parameter changes to handle filtering of cases based on user-selected criteria.
	 */
	ngOnInit() {

		combineLatest([
			this.siteSubscriptions.hierarchicalData$.pipe(distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(distinctUntilChanged()),
			this.cmmsService.getCauseTypes$().pipe(distinctUntilChanged()),
			this.cmmsService.getTriggerTypes$().pipe(distinctUntilChanged()),
			this.route.queryParamMap.pipe(distinctUntilChanged())
		]).pipe(
			takeUntil(this.finalise),
			// Ensure that subscriptions are not null or undefined
			filter(([hierarchicalData, currentUser, causeTypes, triggerTypes, queryParamMap]) =>
				!!hierarchicalData && !!currentUser && !!causeTypes && !!triggerTypes && !!queryParamMap)
		).subscribe({
			next: async ([hierarchicalData, currentUser, causeTypes, triggerTypes, queryParamMap]) => {

				let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
					combineLatest([
						this.siteSubscriptions.selectedAccount$.pipe(take(1)),
						this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
					]
					)
				);

				this.selectedAccount = selectedAccount;
				this.accountGUID = selectedAccountGUID;
				this.organisationUID = selectedOrganisationUID;

				let urlAccountID = this.router.url.split('/')[1];

				if (this.accountGUID.toLowerCase() != urlAccountID.toLowerCase() && this.router.url.includes('actions') && !this.router.url.includes('routing#')) {
					this.router.navigateByUrl(`routing#${this.router.url}`);
				}
				this.hierarchy = JSON.parse(hierarchicalData);

				this.causeTypes = causeTypes;
				this.triggerTypes = triggerTypes;

				this.currentUser = currentUser;

				/** Default to look for only open cases */
				this.filters = this.globalFunctions.queryParamsToObject<CaseFilters>(
					queryParamMap,
					this.caseFilterTypes
				);
				this.filters.open = this.filters.open ?? true;
				this.filters.CMMS = this.filters.CMMS ?? true;
				this.filters.closed = this.filters.closed ?? false;
				this.filters.NotCMMS = this.filters.NotCMMS ?? false;
				this.filters.all = !this.filters.open && !this.filters.closed && !this.filters.CMMS && !this.filters.NotCMMS;

				if (this.filters.from) {
					this.filters.to = this.filters.to ?? 0
					this.startDate = new Date(this.filters.from);
					this.endDate = new Date(this.filters.to ?? 0);
				} else {
					this.startDate = new Date(this.today)
					this.endDate = new Date(this.today)

					this.startDate.setDate(this.startDate.getDate() - 30);
					this.endDate.setDate(this.endDate.getDate() + 1)

					this.filters.from = this.startDate.getTime();
					this.filters.to = this.endDate.getTime();
				}

				if (this.queryTo != this.filters.to || this.queryFrom != this.filters.from) {
					this.getCases(this.filters.from, this.filters.to);
				}

				this.queryTo = this.filters.to;
				this.queryFrom = this.filters.from ?? 0;
				this.formattedStartDate = makeDateSmall(new Date(this.queryFrom));
				this.formattedEndDate = makeDateSmall(new Date(this.queryTo));

			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * getActions
	 * Retrieves the list of cases from the CMMS service based on the provided time range. 
	 * It uses combineLatest to make parallel API calls to fetch cases, cause types, 
	 * and trigger types, ensuring that all necessary data is available before processing. 
	 * The function also includes error handling to manage any issues that arise during the API calls, 
	 * and it updates the component's state with the retrieved data for display in the UI.
	 * @param timeFrom timeFrom
	 * @param timeTo timeTo
	 */
	async getCases(timeFrom: number = 0, timeTo: number = 0) {

		const queryParams: any = {
			...(timeFrom !== 0 && { From: timeFrom }),
			...(timeTo !== 0 && { To: timeTo })
		};

		this.cmmsCases = [];

		try {
			this.assignTable();

			const cmmsCases = await firstValueFrom(
				this.cmmsService.getCases$(this.accountGUID, this.organisationUID, queryParams)
			);

			this.cmmsCases = cmmsCases;
			this.assignTable();
			this.filterCases()
		}
		catch (error: any) {
			// Handle the error
			if (error?.status === 404) {
				this.globalFunctions.HandleError(error, "this.cmmsService.getCases$: No cases found for the specified criteria.");
			} else {
				this.globalFunctions.HandleError(error, "this.cmmsService.getCases$: An error occurred while retrieving cases.");
			}
		}

	}

	/**
	 * assignTable
	 * Initializes the MatTableDataSource with the filtered cases and sets up pagination and sorting.
	 * It also defines a custom sortingDataAccessor to handle sorting based on specific fields, ensuring that null or undefined 
	 * values are handled gracefully.
	 * This function is called after filtering the cases to update the table display with the current set of filtered cases.
	 */
	assignTable() {
		if (!this.dataSource) {
			this.dataSource = new MatTableDataSource(this.cmmsCases);
		} else {
			this.dataSource.data = this.cmmsCases;
		}

		this.dataSource.filterPredicate = (data: ICaseFile, filter: string) => {
			/** Build a string per row to filter and convert to lower case */

			const showOpen = this.filters.open === true;
			const showClosed = this.filters.closed === true;
			const showCMMS = this.filters.CMMS === true;
			const hideCMMS = this.filters.NotCMMS === true;

			// if (this.filters.all !== true) {
			const statusFilterActive = showOpen || showClosed;
			if (
				statusFilterActive &&
				!(
					(showOpen && data.IsOpen) ||
					(showClosed && !data.IsOpen)
				)
			) {
				return false;
			}

			const cmmsFilterActive = showCMMS || hideCMMS;
			if (
				cmmsFilterActive &&
				!(
					(showCMMS && data.InCMMS) ||
					(hideCMMS && !data.InCMMS)
				)
			) { return false; }
			// }

			const query = filter.toLowerCase().trim();
			const dateStr = `${convertEpochToDateString(data.OpenedAt)}`.toLowerCase();
			const AssetName = `${data.Asset.Name}`.toLowerCase() || '';
			const Desc = `${data.Description}`.toLowerCase() || '';
			const TriggerType = `${this.getTriggerTypeFromValue(data.TriggerType) || ''}`.toLowerCase();
			const FunctionalLocation = `${data.FL}`.toLowerCase() || '';

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${AssetName} ${Desc} ${TriggerType} ${FunctionalLocation}`;
			return combinedStr.includes(query);

		};

		this.dataSource.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'FL': return item.FL || ''; // Ensure no null/undefined values
				case 'AssetID': return item.Asset.Name || ''; // Ensure no null/undefined values
				case 'Cause': return item.Cause || ''; // Use AssetName for the 'asset' column
				case 'OpenedAt': return item.OpenedAt || '';
				case 'CMMSCreatedAt': return item.CMMSCreatedAt || 0;
				default: return '';
			}
		};

		/** Only seems to work if this is done last */
		if (this.paginator) {
			this.dataSource.paginator = this.paginator;
			this.dataSource.sort = this.sort;

			if (this.filters.pageSize) { this.paginator.pageSize = this.filters.pageSize }
			if (this.filters.pageIndex) { this.paginator.pageIndex = this.filters.pageIndex }
		}

	}

	/**
	 * getTriggerTypeFromValue
	 * Takes a numeric value representing a trigger type and returns the corresponding description by searching through the list of trigger types.
	 * The function uses the `find` method to locate the trigger type object with a matching `TriggerType` value and retrieves its `Description`.
	 * If a matching trigger type is found, its description is returned; otherwise, an empty string is returned.
	 * This function is useful for translating numeric trigger type values from the database into human-readable descriptions for display in the UI.
	 * @param value value 
	 * @returns 
	 */
	getTriggerTypeFromValue(value: number): string {
		let result = this.triggerTypes.find((x: iTriggerTypes) => x.TriggerType == value)?.Description;
		if (result) {
			return result;
		} else {
			return "";
		}
	}

	/**
	 * getCauseTypeFromValue
	 * Takes a numeric value representing a cause type and returns the corresponding description by searching through the list of cause types.
	 * The function uses the `find` method to locate the cause type object with a matching `CauseType` value and retrieves its `Description`.
	 * If a matching cause type is found, its description is returned; otherwise, an empty string is returned.
	 * This function is useful for translating numeric cause type values from the database into human-readable descriptions for display in the UI.
	 * @param value value 
	 * @returns 
	 */
	getCauseTypeFromValue(value: number): string {
		let result = this.causeTypes.find((x: iCauseTypes) => x.CauseType == value)?.Description;
		if (result) {
			return result;
		} else {
			return "";
		}
	}

	/**
	 * convertEpoch	 * 
	 * Converts an epoch timestamp to a human-readable date string using a global function.
	 * This function serves as a wrapper around the global `convertEpochToDateString` function, 
	 * allowing for consistent date formatting throughout the component.
	 * By centralizing the date conversion logic in a global function, 
	 * it ensures that all dates are displayed in the same format and makes it easier to 
	 * maintain and update the date formatting logic in one place if needed.
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * goToCaseDetails
	 * Navigates to the case details page for the selected case file. 
	 * It uses Angular's Router to navigate to a route based on the case file's ID, relative to the current route. 
	 * This allows users to view more detailed information about a specific case when they click on it in the list.
	 * @param caseFile action 
	 */
	goToCaseDetails(caseFile: ICaseFile) {
		this.router.navigate([`${caseFile.Id}`], { relativeTo: this.route });
	}

	/**
	 * onFiltering
	 * Filtering function for the search input. It filters the cases based on the search query matching any of the following fields: 
	 * OpenedAt date, Asset Name, Trigger Type, or Functional Location. 
	 * The filtered results are then assigned to the data source for display in the table.
	 * @param event
	 */
	onFilteringActions(event: any): void {
		event.cancel = true;
		const query = event.target.value.toLowerCase();
		this.filters.filter = query;
		this.dataSource.filter = query;
		this.filterCases();
	}

	/** 
	 * Getting list of assets from an input hierarchy 
	 * This function takes a hierarchical structure of organizations and their associated assets, 
	 * and returns a flattened list of all assets contained within that hierarchy. It recursively traverses the hierarchy, 
	 * checking each level for assets and adding them to the result list. If an item in the hierarchy has an 'Assets' 
	 * property with one or more assets, those assets are added to the result. The function then continues to check any 
	 * 'SubChild' items for additional assets, ensuring that all assets from all levels of the hierarchy are included in the final output.
	 * @param hierarchy OrganisationsHierarchy[]
	 * @returns any[]
	 */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * onFilterChange
	 * Handles changes to the filter checkboxes and updates the filtered cases accordingly.
	 * When the "All" filter is selected, it deselects the "Open", "Closed", and "In CMMS" filters.
	 * When any of the "Open", "Closed", or "In CMMS" filters are changed, it deselects the "All" filter if necessary.
	 * After updating the filter states, it calls the filterCases() method to apply the filters to the case list.
	 * @param changed 
	 */
	onFilterChange(changed: keyof typeof this.filters) {

		if (changed === 'all' && this.filters.all) {
			this.filters.open = false;
			this.filters.closed = false;
			this.filters.CMMS = false;
			this.filters.NotCMMS = false;
		}

		if (changed !== 'all') {
			this.filters.all = false;
			if (!this.filters.open && !this.filters.closed && !this.filters.CMMS && !this.filters.NotCMMS) {
				this.filters.all = true;
			}
		}

		if (this.paginator) { this.paginator.pageIndex = 0; }

		this.filterCases();
	}

	/**
	 * DatePickerChange
	 * @param event
	 */
	DatePickerChange(event: any) {
		try {
			event.startDate.setHours(0, 0, 0, 0);
			event.endDate.setHours(0, 0, 0, 0);

			if (event.daySpan == 0) {
				this.startDate.setDate(this.today.getDate() - 30);
				this.endDate = this.today;
				this.filters.to = this.endDate.getTime();
				this.filters.from = this.startDate.getTime();

				if (this.queryTo != this.filters.to || this.queryFrom != this.filters.from) {
					this.filterCases(true);
				}
				return
			}
			this.isFiltering = true;

			const date1 = new Date(event.startDate.getTime());
			const date2 = new Date(event.endDate.getTime());

			const diffInMs = Math.abs(date2.getTime() - date1.getTime());
			const diffInMonths = diffInMs / (1000 * 60 * 60 * 24 * 30.44);

			if (diffInMonths < this.maxMonths) {
				this.startDate = event.startDate;
				this.endDate = event.endDate

				if (this.startDate && this.endDate) {
					this.formattedStartDate = makeDateSmall(this.startDate);
					this.formattedEndDate = makeDateSmall(this.endDate);
					/** Add a day to retrieve data for last day too */
					this.endDate.setDate(this.endDate.getDate() + 1)

					this.filters.from = this.startDate.getTime();
					this.filters.to = this.endDate.getTime();
					if (this.queryTo != this.filters.to || this.queryFrom != this.filters.from) {
						this.filterCases(true);
					}
				}
			}
		}
		catch (error) { this.globalFunctions.HandleError(error, "DatePickerChange Error: Unknown", false) }
	}

	/**
	 * Rebuild an URL for refreshing page with new query params
	 * This function uses Angular's Router to navigate to the current route while merging the existing query parameters with the new filter parameters. 
	 * By calling this function after applying filters, the URL is updated to reflect the current filter state, allowing users to bookmark or share the filtered view. 
	 * It also ensures that the filter state is preserved when the page is reloaded, providing a consistent user experience.
	 */
	rebuildURL(reQuery: boolean): void {
		if (reQuery) {
			this.router.navigate([], { queryParams: this.filters, queryParamsHandling: 'merge' });
		} else {
			const urlTree = this.router.createUrlTree([], {
				queryParams: this.filters,
				queryParamsHandling: 'merge'
			});

			window.history.replaceState(
				window.history.state,
				'',
				this.router.serializeUrl(urlTree)
			);
		}
	}

	/**
	 * filterCases
	 * Applies the selected filters to the list of cases and updates the filtered cases accordingly.
	 */
	filterCases(reQuery: boolean = false) {
		if (this.dataSource) {
			const currentFilter = this.dataSource.filter;
			this.dataSource.filter = '';
			this.dataSource.filter = currentFilter || this.filters.filter || ' ';
		}
		this.isFiltering = !this.filters.all || this.isFiltering;

		this.rebuildURL(reQuery);
	}

	/**
	 * onPageChange
	 */
	onPageChange(event: PageEvent): void {
		this.filters.pageSize = this.paginator.pageSize;
		this.filters.pageIndex = this.paginator.pageIndex;
		this.filterCases()
	}
}
